import { useEffect, useRef, useState } from 'react';

// The portal's database is serverless and pauses after an hour idle, so the
// first request after a quiet spell waits for it to resume — sometimes under a
// second, occasionally the better part of a minute. Until now every one of
// those waits looked identical to the user: a bare "Loading…" with no end in
// sight. On 2026-09-02 someone sat through 13.7s of it, reloaded, gave up
// after another 8.2s, and came back five minutes later. The API never errored;
// the page just never said anything.
//
// So this escalates. A fast load looks exactly as it always did — no alarm for
// the common case — and only a wait long enough to feel broken earns an
// explanation and, most importantly, "don't refresh": reloading restarts the
// wait the user is already most of the way through.

const EXPLAIN_AFTER_MS = 3_000;
const REASSURE_AFTER_MS = 20_000;

// Fills quickly at first, then slows, approaching but never reaching the end.
// A bar that completes while the user is still waiting is worse than no bar,
// and we genuinely cannot know how long a resume will take — this promises
// progress, not arrival.
const PROGRESS_CEILING = 0.92;
const PROGRESS_TAU_MS = 18_000;

export interface BootPhase {
  headline: string;
  detail: string | null;
  /** 0–1, or null before the bar is shown at all. */
  progress: number | null;
}

export function bootPhase(elapsedMs: number): BootPhase {
  if (elapsedMs < EXPLAIN_AFTER_MS) {
    return { headline: 'Loading…', detail: null, progress: null };
  }

  const progress = PROGRESS_CEILING * (1 - Math.exp(-elapsedMs / PROGRESS_TAU_MS));

  if (elapsedMs < REASSURE_AFTER_MS) {
    return {
      headline: 'Waking up the portal',
      detail: 'It has been quiet for a while, so this can take up to a minute. No need to refresh — that starts the wait over.',
      progress,
    };
  }

  return {
    headline: 'Still waking up',
    detail: 'Longer than usual, but it is working. Refreshing will not make it faster.',
    progress,
  };
}

export default function PortalLoading() {
  const startedAt = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setElapsed(Date.now() - startedAt.current), 250);
    return () => clearInterval(id);
  }, []);

  const { headline, detail, progress } = bootPhase(elapsed);

  return (
    <div style={{ padding: 32, maxWidth: 460 }}>
      <div
        aria-live="polite"
        style={{
          color: detail ? 'var(--mi-navy)' : 'var(--mi-text)',
          fontWeight: detail ? 600 : 400,
          fontSize: detail ? 17 : 15,
        }}
      >
        {headline}
      </div>

      {progress !== null && (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          aria-label="Waking up the portal"
          style={{
            marginTop: 14,
            height: 4,
            borderRadius: 2,
            background: 'var(--mi-border)',
            overflow: 'hidden',
          }}
        >
          <div
            className="mi-boot-bar"
            style={{
              width: `${progress * 100}%`,
              height: '100%',
              background: 'var(--mi-navy)',
              borderRadius: 2,
            }}
          />
        </div>
      )}

      {detail && (
        <p style={{ marginTop: 12, color: 'var(--mi-muted)', fontSize: 13, lineHeight: 1.5 }}>
          {detail}
        </p>
      )}
    </div>
  );
}
